import { useCallback, useMemo } from "react";
import React from "react";

import { Rename } from "components/Prompts";
import { useIsViewerPermission, useModals, usePrompt, useRoleGuardHook } from "hooks";
import { useDealActions, useViewActions } from "store";
import { IFormMetaDetail } from "store/types";
import { IItemToShare } from "store/types/deal/share";

import { EntityType, RoleName } from "../../../constants";
import { ContextMenuOptionObjectType } from "../../popups/Context-menu/Options-collection";
import { useWorkspace } from "../WorkspaceContext";

export interface UseWorkspaceFormHookParams {
  form: IFormMetaDetail;
  refetchHandler?: () => Promise<void>;
}

export const useWorkspaceFormHook = ({ form, refetchHandler }: UseWorkspaceFormHookParams) => {
  const dealId = form.deal?.id ?? null;
  const openPrompt = usePrompt();
  const { deleteModal, errorModal } = useModals();
  const { setShareDialog } = useViewActions();
  const { updateFormAction, deleteFormAction, duplicateFormAction } = useDealActions();
  const { dispatchDetail } = useWorkspace();

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const handleShare = useCallback(() => {
    setShareDialog({
      elements: [form.id],
      type: "form_id",
      holderId: form.section_id,
      shareName: form.title,
      dealId: dealId ?? null,
      itemToShare: form.is_private ? null : (form as unknown as IItemToShare),
      cb: async () => {
        await refetchHandler?.();
      }
    });
  }, [setShareDialog, form, dealId, refetchHandler]);

  const handleDelete = useCallback(() => {
    deleteModal({
      element: "Delete item permanently?",
      message: "This cannot be undone",
      onPressSuccess: "Delete permanently",
      action: async () => {
        await deleteFormAction({ dealId: dealId ?? undefined, formId: form.id });
        dispatchDetail({
          key: "delete",
          id: form.id,
          entityType: EntityType.FORM
        });
      }
    });
  }, [deleteModal, deleteFormAction, form, dealId, dispatchDetail]);

  const handleRename = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (!form.id) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: form.title }
      });
      if (title) {
        await updateFormAction({ formId: form.id, dealId: dealId ?? undefined, title });
        dispatchDetail({
          key: "patch",
          id: form.id,
          entityType: EntityType.FORM,
          patch: { title }
        });
      }
    },
    [form.id, form.title, dealId, openPrompt, updateFormAction, dispatchDetail]
  );

  const handleDuplicate: (() => Promise<void>) | ContextMenuOptionObjectType = useMemo(
    () =>
      form.kind === "static"
        ? async () => {
            await duplicateFormAction({ formId: form.id });
            await refetchHandler?.();
          }
        : {
            action: async (value: string) => {
              try {
                await duplicateFormAction({
                  formId: form.id,
                  recurringDate: value
                });
                await refetchHandler?.();
              } catch (error) {
                errorModal((error as any)?.errors?.recurring_date);
                throw error;
              }
            },
            withDatePicker: true
          },
    [form.kind, form.id, duplicateFormAction, refetchHandler, errorModal]
  );

  const handleConvertToPeriodic: ContextMenuOptionObjectType | undefined = useMemo(() => {
    if (form.kind === "static") {
      return {
        action: async (value: string) => {
          try {
            await updateFormAction({
              formId: form.id,
              dealId: dealId ?? undefined,
              kind: "recurring",
              recurring_date: value
            });
            dispatchDetail({
              key: "patch",
              id: form.id,
              entityType: EntityType.FORM,
              patch: { kind: "recurring" }
            });
          } catch (error) {
            errorModal((error as any)?.errors?.recurring_date);
            throw error;
          }
        },
        withDatePicker: true
      };
    }
  }, [form.kind, form.id, dealId, updateFormAction, dispatchDetail, errorModal]);

  const handleCancelPeriodic: (() => Promise<void>) | undefined = useMemo(() => {
    if (form.kind === "recurring") {
      return async () => {
        await updateFormAction({ formId: form.id, dealId: dealId ?? undefined, kind: "static" });
        dispatchDetail({
          key: "patch",
          id: form.id,
          entityType: EntityType.FORM,
          patch: { kind: "static" }
        });
      };
    }
  }, [form.kind, form.id, dealId, updateFormAction, dispatchDetail]);

  const isAvailableRoleWithPermissions = isAnalystDealsViewerPermission
    ? [RoleName.Admin]
    : [RoleName.Admin, RoleName.Analyst];

  const handleShareWithRole = useRoleGuardHook(handleShare, isAvailableRoleWithPermissions);
  const handleDeleteWithRole = useRoleGuardHook(handleDelete, isAvailableRoleWithPermissions);
  const handleRenameWithRole = useRoleGuardHook(handleRename, isAvailableRoleWithPermissions);
  const handleDuplicateWithRole = useRoleGuardHook(handleDuplicate, isAvailableRoleWithPermissions);
  const handleConvertToPeriodicWithRole = useRoleGuardHook(handleConvertToPeriodic, isAvailableRoleWithPermissions);
  const handleCancelPeriodicWithRole = useRoleGuardHook(handleCancelPeriodic, isAvailableRoleWithPermissions);

  return {
    handleShare: handleShareWithRole,
    handleDelete: handleDeleteWithRole,
    handleRename: handleRenameWithRole,
    handleDuplicate: handleDuplicateWithRole,
    handleConvertToPeriodic: handleConvertToPeriodicWithRole,
    handleCancelPeriodic: handleCancelPeriodicWithRole
  };
};
